import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filtermodels, filtermodel } from '../../../model/sort-filter';

@Component({
  selector: 'app-active-filters',
  templateUrl: './active-filters.component.html',
  styleUrls: ['./active-filters.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule
  ]
})
export class ActiveFiltersComponent {
  @Input() filters: filtermodels = {};
  @Input() headerMap: Map<string, any> = new Map();
  @Input() defaultFilters: filtermodels = {}; // Default filters for reset functionality - TK 01/15/2025
  @Input() title = 'Active Filters'; // Customizable title - TK 01/15/2025
  @Input() showClearAll = true; // Option to hide clear all button - TK 01/15/2025
  @Input() showFilterButton = true; // Option to show/hide filter button - TK 01/15/2025
  @Input() filterButtonTooltip = 'Filter all columns'; // Customizable tooltip - TK 01/15/2025
  @Output() clearAll = new EventEmitter<void>();
  @Output() clearFilter = new EventEmitter<string>();
  @Output() openFilterDialog = new EventEmitter<void>(); // New output for filter button - TK 01/15/2025

  isExpanded = false; // Track expansion state - TK 01/15/2025

  get activeFiltersCount(): number {
    return Object.keys(this.filters).length;
  }

  get hasActiveFilters(): boolean {
    return this.activeFiltersCount > 0;
  }

  getFilterDisplayValue(column: string, filter: filtermodel): string {
    const columnLabel = this.headerMap.get(column)?.label || column;

    if (filter.filterType === 'date') {
      if (filter.dateFrom && filter.dateTo) {
        return `${columnLabel}: ${filter.dateFrom} to ${filter.dateTo}`;
      } else if (filter.dateFrom) {
        return `${columnLabel}: From ${filter.dateFrom}`;
      } else if (filter.dateTo) {
        return `${columnLabel}: To ${filter.dateTo}`;
      }
    } else {
      return `${columnLabel}: ${filter.filter}`;
    }

    return `${columnLabel}: ${filter.filter}`;
  }

  isDefaultFilter(column: string): boolean {
    // Check if this filter matches the default filter for this column - TK 01/15/2025
    const defaultFilter = this.defaultFilters[column];
    if (!defaultFilter) return false;

    const currentFilter = this.filters[column];
    if (!currentFilter) return false;

    // Compare filter values to determine if it's a default filter - TK 01/15/2025
    return JSON.stringify(currentFilter) === JSON.stringify(defaultFilter);
  }

  onClearFilter(column: string): void {
    // Prevent clearing default filters - TK 01/15/2025
    if (!this.isDefaultFilter(column)) {
      this.clearFilter.emit(column);
    }
  }

  onClearAll(): void {
    // Emit event with default filters to reset to defaults - TK 01/15/2025
    this.clearAll.emit();
  }

  onOpenFilterDialog(): void {
    // Emit event to open filter dialog - TK 01/15/2025
    this.openFilterDialog.emit();
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded; // Toggle expansion state - TK 01/15/2025
  }

  getFilterEntries(): Array<{key: string, value: filtermodel}> {
    return Object.entries(this.filters).map(([key, value]) => ({ key, value }));
  }
}
